import Star from 'assets/Icons/Star';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import React, { forwardRef } from 'react';
import {
  BiCopy,
  BiDislike,
  BiLike,
  BiSolidDislike,
  BiSolidLike,
} from 'react-icons/bi';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { twMerge } from 'tailwind-merge';

import { EventDetails, logEvent } from '@/hooks/useClickEventTracking';

import RenderIf from '@/components/Common/RenderIf';
import CitationPopover from '@/components/ZAIChatLayout/CitationPopover';

import { user } from '@/atoms/user';
import { User } from '@/utils/interfaces/User';

interface CitationGroupProps {
  ids: number[];
  sources: any[];
}

const CitationGroup: React.FC<CitationGroupProps> = ({ ids, sources }) => {
  return (
    <span className='inline align-middle'>
      {ids.map((id) => {
        const sourceDocument = sources.find((s) => s.citation_id === id);

        if (!sourceDocument) return null;

        return (
          <CitationPopover key={id} id={id} sourceDocument={sourceDocument}>
            <span
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open(
                    `/books/${sourceDocument.metadata.slug}-${sourceDocument.metadata.book_id}?utm_source=zai`,
                    '_blank',
                    'noopener,noreferrer',
                  );
                }
              }}
              style={{
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,"Helvetica Neue", Arial, sans-serif',
              }}
              className='tracking-[2%] me-1 relative top-0.5 last:me-0.5 inline-block px-2 py-1 text-[10px]/[12px] rounded bg-grayZl-900 cursor-pointer hover:bg-grayZl-850 transition line-clamp-1'
            >
              {sourceDocument?.metadata?.book_name?.trim() +
                ', Pg. ' +
                sourceDocument?.metadata?.page}
            </span>
          </CitationPopover>
        );
      })}
    </span>
  );
};

const extractCitationIdsFromText = (text: string): number[] => {
  const ids = new Set<number>();

  const regex = /citation:(\d+)|\[(\d+)\]/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const id = Number(match[1] || match[2]);
    if (!isNaN(id)) {
      ids.add(id);
    }
  }

  return Array.from(ids);
};

const flattenText = (children: React.ReactNode): string => {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(flattenText).join('');
  if (typeof children === 'object' && children !== null && 'props' in children)
    return flattenText((children as any).props.children);
  return '';
};
const citationRegex = /\[\[((?:\s*\[?citation:\d+\]?\s*,?)+)\]\]|\[(\d+)\]/g;

const renderWithCitations = (text: string, sources: any[]) => {
  const parts: (string | number[])[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = citationRegex.exec(text)) !== null) {
    parts.push(text.slice(lastIndex, match.index));

    let ids: number[] = [];

    if (match[1]) {
      // Handles:
      // [[citation:5], [citation:9]]
      // [[citation:5, citation:9]]
      // [[citation:10]]
      ids = match[1]
        .match(/citation:(\d+)/g)!
        .map((c) => Number(c.replace('citation:', '')));
    } else if (match[2]) {
      // [10]
      ids = [Number(match[2])];
    }

    parts.push(ids);
    lastIndex = match.index + match[0].length;
  }

  parts.push(text.slice(lastIndex));

  return parts.map((part, i) =>
    Array.isArray(part) ? (
      <CitationGroup key={i} ids={part} sources={sources} />
    ) : (
      <span key={i}>{part}</span>
    ),
  );
};

const copyMessage = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success('Message copied');
  } catch (err) {
    console.error('Copy failed', err);
  }
};

export type ChatRole = 'user' | 'assistant';

export interface ISourceDocuments {
  citation_id: number;
  metadata: ISourceDocumentsMetadata;
  type: string;
}

export interface ISourceDocumentsMetadata {
  author_67723214d2cb369e91d9f94a: string;
  book_id: string;
  book_image: string;
  book_name: string;
  book_type: string;
  category_663f49f60253198962b500e1: string;
  document_type: string;
  page: number;
  short_description: string;
  total_pages: number;
  slug: number;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  feedback: number;
  sourceDocuments: ISourceDocuments[];
}

const statusVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};

interface ChatMessagesProps {
  showSources?: boolean;
  messages: ChatMessage[];
  loading?: boolean;
  setShowSources?: React.Dispatch<React.SetStateAction<boolean>>;
  streamAnswer?: string;
  streamStatus?: string | null;
  finalMessageRef?: React.MutableRefObject<any>;
  streamSources?: any[];
  setSourceDocumentsToDisplay?: React.Dispatch<
    React.SetStateAction<ISourceDocuments[]>
  >;
  onFeedback?: (messageId: string, value: -1 | 0 | 1) => void;
}

const messageAnim = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0 },
};

const extractQueryId = (compositeId?: string): string | null => {
  if (!compositeId) return null;

  if (!compositeId.includes('-')) {
    return compositeId;
  }

  const parts = compositeId.split('-');

  if (parts.length < 3) return null;

  return parts[1] || null;
};

const extractSessionId = (compositeId?: string): string | null => {
  if (!compositeId) return null;

  if (!compositeId.includes('-')) {
    return compositeId;
  }

  const parts = compositeId.split('-');
  if (parts.length < 3) return null;
  return parts[0] || null;
};

const getUserQuestionForAssistant = (
  messages: ChatMessage[],
  assistantMsgId: string,
): string | null => {
  const index = messages.findIndex((m) => m.id === assistantMsgId);

  if (index <= 0) return null;

  const prev = messages[index - 1];
  return prev?.role === 'user' ? prev.content : null;
};

const ChatMessages = forwardRef<HTMLDivElement, ChatMessagesProps>(
  (
    {
      messages,
      streamAnswer,
      streamStatus,
      streamSources,
      loading,
      setSourceDocumentsToDisplay,
      setShowSources,
      onFeedback,
      showSources,
    },
    bottomRef,
  ) => {
    const userData = useRecoilValue<User | undefined>(user);
    const router = useRouter();

    const logFeedBack = ({
      eventName,
      sessionId,
      queryId,
      question,
      answer,
    }: {
      eventName: string;
      sessionId: string;
      queryId: string;
      question: string | null;
      answer: string;
    }) => {
      const eventParams: EventDetails = {
        event_name: eventName,
        source: 'ask_ai',
        source_type: 'global',
        source_id: sessionId,
        sub_source: answer,
        sub_source_id: queryId,
        unit: 'boolean',
        value: 1,
        meta: {
          click_source: 'ask_ai_global_screen',
          session_id: sessionId,
          ai_query: question,
        },
      };

      logEvent({
        eventDetails: eventParams,
        userData,
        pathname: router.pathname,
      });
    };

    const handleFeedback = async (msg: ChatMessage, value: -1 | 0 | 1) => {
      if (msg.feedback === value) return;

      const sessionId = extractSessionId(msg.id);
      const queryId = extractQueryId(msg.id);

      if (!sessionId || !queryId) return;

      const question = getUserQuestionForAssistant(messages, msg.id);
      if (!queryId) return;

      try {
        onFeedback?.(msg.id, value);

        logFeedBack({
          eventName:
            value === 1
              ? 'like'
              : value === -1
                ? 'dislike'
                : 'ask_ai_feedback_reset',
          sessionId,
          queryId,
          question,
          answer: msg.content,
        });
      } catch (err) {
        msg.feedback = 0;
      }
    };

    const usedCitations = (usedCitationIds: any, msg: ChatMessage) => {
      const data = msg.sourceDocuments.filter((source) =>
        usedCitationIds.includes(source.citation_id),
      );

      setSourceDocumentsToDisplay?.(data);
    };

    const markdownComponents = (sources?: any[]) => ({
      blockquote: ({ children }: any) => (
        <blockquote className='border-l-[2px] border-grayZl-700 pl-4 my-4 text-base/[24px] font-normal'>
          {children}
        </blockquote>
      ),
      hr: () => <hr className='h-px w-full my-3 bg-grayZl-850 border-0' />,

      h1: ({ children }: any) => (
        <h1 className='sm:text-2xl text-xl/8 sm:font-bold font-semibold text-white mb-3 last:mb-0'>
          {children}
        </h1>
      ),

      h2: ({ children }: any) => (
        <h2 className='sm:text-xl text-[17px]/[28px] font-semibold text-white mb-3 last:mb-0'>
          {children}
        </h2>
      ),

      h3: ({ children }: any) => (
        <h3 className='sm:text-lg/[26px] text-base/[24px] font-semibold text-white mb-3 last:mb-0'>
          {children}
        </h3>
      ),

      h4: ({ children }: any) => (
        <h4 className='sm:text-base/[24px] text-[15px]/[22px] font-semibold text-white mb-3 last:mb-0'>
          {children}
        </h4>
      ),

      p: ({ children }: any) => {
        const text = flattenText(children);

        return (
          <p className='mb-3 last:mb-0 text-white sm:text-base/[24px] text-[15px]/[24px] font-light'>
            {sources ? renderWithCitations(text, sources) : children}
          </p>
        );
      },

      table: ({ children }: any) => (
        <div className='relative mt-3 mb-4 max-w-full overflow-x-auto overscroll-x-contain'>
          <table className='w-full border-collapse text-base'>{children}</table>
        </div>
      ),

      thead: ({ children }: any) => (
        <thead className='bg-grayZl-900'>{children}</thead>
      ),

      tr: ({ children }: any) => (
        <tr className='border-b border-grayZl-850'>{children}</tr>
      ),

      th: ({ children }: any) => (
        <th className='sm:px-4 px-3 sm:py-3 py-1.5 text-left font-semibold sm:text-base text-sm/[22px] text-white'>
          {children}
        </th>
      ),

      td: ({ children }: any) => (
        <td className='sm:px-4 px-3 sm:py-3 py-3 text-white align-top sm:text-base text-sm/[22px]'>
          {children}
        </td>
      ),

      pre: ({ children }: any) => (
        <div className='my-4 max-w-full overflow-auto'>
          <pre className='min-w-full whitespace-pre bg-[#0E0E10] p-4 rounded-lg text-sm font-mono text-gray-200 border border-grayZl-800 overflow-auto'>
            {children}
          </pre>
        </div>
      ),

      code: ({ inline, children }: any) => {
        if (inline) {
          return (
            <code className='bg-[#1E1E1F] px-1.5 py-0.5 rounded text-xs'>
              {children}
            </code>
          );
        }

        return (
          <code className='font-mono text-sm text-gray-200'>{children}</code>
        );
      },

      ul: ({ children }: any) => (
        <ul className='list-disc pl-6 mb-3 space-y-3 text-white sm:text-base/[26px] text-[15px]/[24px] font-light'>
          {children}
        </ul>
      ),

      ol: ({ children }: any) => (
        <ul className='list-decimal pl-6 mb-3 space-y-3 text-white sm:text-base/[26px] text-[15px]/[24px] font-light'>
          {children}
        </ul>
      ),

      li: ({ children }: any) => {
        const text = flattenText(children);

        return (
          <li>{sources ? renderWithCitations(text, sources) : children}</li>
        );
      },

      strong: ({ children }: any) => (
        <strong className='font-semibold text-white'>{children}</strong>
      ),
    });

    return (
      <div className='w-full 2xl:max-w-3xl xl:max-w-[640px] lg:max-w-[640px] md:max-w-[476px] max-w-[640px] mx-auto md:px-3 px-4'>
        <div className='flex flex-col gap-6 py-6 w-full '>
          {messages.map((msg) => {
            const isUser = msg.role === 'user';

            return (
              <motion.div
                key={msg.id}
                initial='hidden'
                animate='visible'
                variants={messageAnim}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={twMerge(
                  clsx(
                    'flex ',
                    isUser
                      ? 'justify-end w-fit max-w-[80%] self-end'
                      : 'justify-start w-full',
                  ),
                  'relative',
                )}
              >
                {!isUser && (
                  <div
                    className={twMerge(
                      'w-10 h-10 relative me-3 ',
                      showSources ? '' : 'lg:absolute lg:top-0 lg:left-[-52px]',
                    )}
                  >
                    <div className='z-[5] absolute top-0 left-0 w-full h-full flex items-center justify-center'>
                      <Star className='w-8 h-8 ' />
                    </div>
                    <div className='z-[4] absolute top-0 left-0 w-full h-full flex items-center justify-center invisible'>
                      <div className='w-[38px] h-[38px] bg-grayZl-950 rounded-full ' />
                    </div>
                    <div className='z-[3] absolute top-0 left-0 w-full h-full flex items-center justify-center invisible'>
                      <div className='animate-spin  w-full h-full bg-gradient-to-t from-grayZl-950 via-grayZl-950/90 to-[#519CF6] rounded-full' />
                    </div>
                  </div>
                )}

                <div
                  className={clsx(
                    'flex-1 md:text-base text-sm leading-6 overflow-auto overflow-y-hidden',
                    isUser
                      ? 'bg-dropDownMenuBg text-white rounded-lg px-3 py-2.5'
                      : 'text-[#E5E5E5]',
                  )}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={markdownComponents(msg.sourceDocuments)}
                  >
                    {msg.content}
                  </ReactMarkdown>

                  {!isUser && (
                    <div className='flex gap-4 mt-2'>
                      <motion.button
                        onClick={() => copyMessage(msg.content)}
                        whileTap={{ scale: 0.85 }}
                        whileHover={{ scale: 1.1 }}
                        disabled={msg.feedback === 1}
                        className={clsx(
                          'transition',
                          msg.feedback === 1
                            ? 'text-white'
                            : 'text-gray-500 hover:text-white',
                        )}
                      >
                        <BiCopy className='text-white w-5 h-5' />
                      </motion.button>
                      <motion.button
                        whileTap={{ scale: 0.85 }}
                        whileHover={{ scale: 1.1 }}
                        disabled={msg.feedback === 1}
                        onClick={() => handleFeedback(msg, 1)}
                        className={clsx(
                          'transition',
                          msg.feedback === 1
                            ? 'text-white'
                            : 'text-gray-500 hover:text-white',
                        )}
                      >
                        {msg.feedback === 1 ? (
                          <BiSolidLike className='text-white w-5 h-5' />
                        ) : (
                          <BiLike className='text-white w-5 h-5' />
                        )}
                      </motion.button>

                      <motion.button
                        whileTap={{ scale: 0.85 }}
                        whileHover={{ scale: 1.1 }}
                        disabled={msg.feedback === -1}
                        onClick={() => handleFeedback(msg, -1)}
                      >
                        {msg.feedback === -1 ? (
                          <BiSolidDislike className='text-white w-5 h-5' />
                        ) : (
                          <BiDislike className='text-white w-5 h-5' />
                        )}
                      </motion.button>
                      <RenderIf
                        condition={
                          !!extractCitationIdsFromText(msg.content).length
                        }
                      >
                        <div
                          onClick={() => {
                            setShowSources?.(true);
                            usedCitations(
                              extractCitationIdsFromText(msg.content),
                              msg,
                            );
                          }}
                          className='text-sm text-grayZl-25 font-medium px-2.5 py-1 bg-grayZl-850 rounded'
                        >
                          {extractCitationIdsFromText(msg.content).length}{' '}
                          Sources
                        </div>
                      </RenderIf>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}

          <RenderIf condition={loading ?? false}>
            <motion.div
              initial='hidden'
              animate='visible'
              variants={messageAnim}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={clsx('flex w-full', 'justify-start relative')}
            >
              <div
                className={twMerge(
                  'w-10 h-10 relative me-3 ',
                  showSources ? '' : 'lg:absolute lg:top-0 lg:left-[-52px]',
                )}
              >
                <div className='z-[5] absolute top-0 left-0 w-full h-full flex items-center justify-center'>
                  <Star className='w-8 h-8 ' />
                </div>
                <div className='z-[4] absolute top-0 left-0 w-full h-full flex items-center justify-center'>
                  <div className='w-[38px] h-[38px] bg-grayZl-950 rounded-full' />
                </div>
                <div className='z-[3] absolute top-0 left-0 w-full h-full flex items-center justify-center'>
                  <div className='animate-spin  w-full h-full bg-gradient-to-t from-grayZl-950 via-grayZl-950/90 to-[#519CF6] rounded-full' />
                </div>
              </div>

              <div
                className={clsx(
                  'flex-1 md:text-base text-sm leading-6 overflow-y-hidden',
                  'text-[#E5E5E5]',
                )}
              >
                <RenderIf condition={!!streamStatus && !streamAnswer}>
                  <motion.div
                    key={streamStatus} // 👈 THIS is critical
                    variants={statusVariants}
                    initial='initial'
                    animate='animate'
                    exit='exit'
                    transition={{
                      duration: 0.25,
                      ease: 'easeOut',
                    }}
                    className='md:text-base text-sm text-white px-3 py-[9px] bg-stroke rounded-lg self-start w-fit'
                  >
                    {streamStatus}
                  </motion.div>
                </RenderIf>
                <RenderIf condition={!!streamAnswer}>
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.25,
                      ease: 'easeOut',
                    }}
                    className='relative'
                  >
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={markdownComponents(streamSources)}
                    >
                      {streamAnswer}
                    </ReactMarkdown>
                  </motion.div>
                </RenderIf>
              </div>
            </motion.div>
          </RenderIf>
          <div ref={bottomRef as React.RefObject<HTMLDivElement>} />
        </div>
      </div>
    );
  },
);

export default ChatMessages;
